import { Navigate, Route, Routes, Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, signUpRequest } from './authConfig';
import { TenantListPage } from './pages/TenantListPage';
import { TenantOnboardingPage } from './pages/TenantOnboardingPage';
import { TenantDetailPage } from './pages/TenantDetailPage';
import { JobsPage } from './pages/JobsPage';
import { BillingPage } from './pages/BillingPage';
import { CancellationsPage } from './pages/CancellationsPage';
import { ManagementLayout } from './pages/ManagementLayout';
import { CompsPage } from './pages/CompsPage';
import { ReconciliationPage } from './pages/ReconciliationPage';
import { TenantControlsPage } from './pages/TenantControlsPage';
import { RetentionPage } from './pages/RetentionPage';
import { AuditPage } from './pages/AuditPage';
import { useMe, useIsAdmin } from './useMe';
import { EntityGridPage } from './pages/EntityGridPage';
import { MergePage } from './pages/MergePage';
import { DuplicatesPage } from './pages/DuplicatesPage';

function SignInScreen() {
  const { instance, inProgress } = useMsal();

  // "Create an account" on acutidy.com arrives as ?signup=1 and goes straight
  // to the account-creation form, so the marketing button does what it says
  // instead of handing over to a sign-in screen.
  //
  // ⚠ THE PARAMETER IS STRIPPED BEFORE REDIRECTING, and that is the whole
  // safety of it. If it survived, cancelling sign-up would return the customer
  // to this URL, which would immediately send them back — a loop with no way
  // out except closing the tab. Removing it first means cancel lands here, on
  // a page with two buttons and a choice.
  //
  // Guarded on inProgress: MSAL throws if interaction is already under way,
  // which is exactly the state a customer is in while returning from Entra.
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('signup')) return;

    params.delete('signup');
    const query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));

    void instance.loginRedirect(signUpRequest);
  }, [instance, inProgress]);

  return (
    <main className="centred">
      <div className="card">
        <h1 className="brand-title">
          Acu<span className="brand-accent">Tidy</span>
        </h1>
        <p className="muted">
          Bulk data cleanup and mass-edit for Acumatica ERP.
        </p>
        {/* Not "Sign in with Microsoft" any more. Customers now create an
            AcuTidy account with an email address and a password in our own
            Entra External ID directory — they are not signing in with a
            Microsoft work account, and saying so sent people looking for
            credentials they do not have.

            TWO buttons, not one, because they now go to different places:
            signing up uses prompt=create and lands on the account-creation
            form, while signing in lands on the sign-in form. Until 14 September
            2026 a single button sent everyone to the sign-in form, where
            registering meant spotting a quiet "No account? Create one" link. */}
        <div className="toolbar">
          <button
            type="button"
            className="primary"
            onClick={() => void instance.loginRedirect(signUpRequest)}
          >
            Create an account
          </button>
          <button type="button" onClick={() => void instance.loginRedirect(loginRequest)}>
            Sign in
          </button>
        </div>
      </div>
    </main>
  );
}

/**
 * Sends non-admins to the tenant list instead of showing them a support screen.
 *
 * Cosmetic, NOT the security boundary — every admin route is enforced
 * server-side by authenticateAdmin, which 403s without the role. This just
 * stops a customer who guesses the URL from landing on an internal-looking
 * page with an error on it.
 *
 * Renders nothing while the answer is in flight. Redirecting on a default of
 * "not an admin" would bounce admins off their own page in the moment before
 * /me returns.
 */
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { me, loading } = useMe();
  if (loading) return null;
  if (!me?.roles?.includes('Admin')) return <Navigate to="/tenants" replace />;
  return <>{children}</>;
}

function Header() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const isAdmin = useIsAdmin();

  return (
    <header className="app-header">
      <Link to="/tenants" className="brand">
        <span className="brand-word">
          Acu<span className="brand-accent">Tidy</span>
        </span>
      </Link>
      <div className="header-right">
        {/* Support surface. Hidden from customers as a courtesy, not as
            security — the API enforces the role on every admin route. */}
        {isAdmin && (
          <Link to="/manage/cancellations" className="muted">
            Management
          </Link>
        )}
        <span className="muted">{account?.name ?? account?.username}</span>
        <button
          type="button"
          className="link-button"
          onClick={() => void instance.logoutRedirect()}
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

/**
 * Entity grids are data screens and get the full viewport; everything else is
 * a form or a summary and keeps the narrow, readable column. Driven off the
 * route rather than a prop so the layout decision lives in one place instead
 * of every page having to remember to ask for it.
 */
function useWideLayout(): boolean {
  const { pathname } = useLocation();
  return /\/entities\/[^/]+$/.test(pathname);
}

export function App() {
  const wide = useWideLayout();

  return (
    <>
      <UnauthenticatedTemplate>
        <SignInScreen />
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <Header />
        <main className={wide ? 'app-main wide' : 'app-main'}>
          <Routes>
            <Route path="/" element={<Navigate to="/tenants" replace />} />
            <Route path="/tenants" element={<TenantListPage />} />
            <Route path="/tenants/new" element={<TenantOnboardingPage />} />
            <Route path="/tenants/:tenantId" element={<TenantDetailPage />} />
            <Route path="/tenants/:tenantId/jobs" element={<JobsPage />} />
            {/*
              Stripe redirects here from Checkout and from the billing portal.
              This route must keep existing and keep this exact path — the URLs
              are baked into success_url/cancel_url/return_url on the API side,
              and the catch-all below would swallow them silently.
            */}
            <Route path="/tenants/:tenantId/billing" element={<BillingPage />} />
            {/* Kept so an existing bookmark or link does not 404. */}
            <Route
              path="/support/cancellations"
              element={<Navigate to="/manage/cancellations" replace />}
            />
            <Route
              path="/manage"
              element={
                <RequireAdmin>
                  <ManagementLayout />
                </RequireAdmin>
              }
            >
              <Route index element={<Navigate to="/manage/cancellations" replace />} />
              <Route path="cancellations" element={<CancellationsPage />} />
              <Route path="comps" element={<CompsPage />} />
              <Route path="reconciliation" element={<ReconciliationPage />} />
              <Route path="tenants" element={<TenantControlsPage />} />
              <Route path="retention" element={<RetentionPage />} />
              <Route path="audit" element={<AuditPage />} />
            </Route>
            <Route
              path="/tenants/:tenantId/entities/:entity/duplicates"
              element={<DuplicatesPage />}
            />
            <Route
              path="/tenants/:tenantId/entities/:entity/duplicates/:candidateId/merge"
              element={<MergePage />}
            />
            <Route
              path="/tenants/:tenantId/entities/:entity"
              element={<EntityGridPage />}
            />
            <Route path="*" element={<Navigate to="/tenants" replace />} />
          </Routes>
        </main>
      </AuthenticatedTemplate>
    </>
  );
}
